import { useUser } from '@folklore/auth';
import { useForm } from '@folklore/forms';
import classNames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';
import { useEffect, useRef, useState } from 'react';

import { useAuthLogout } from '../../hooks/useAuth';
import { CreateCommentRequest, useCreateComment, useUpdateComment } from '../../hooks/useComments';

import { useSetting } from '../../contexts/SiteContext';
import AvatarButton from '../buttons/AvatarButton';
import FormButton from '../buttons/FormButton';
import RoundedButton from '../buttons/RoundedButton';
import TextField from '../fields/TextField';
import TextareaField from '../fields/TextareaField';
import AccountIcon from '../icons/AccountIcon';
import CommentAvatar from '../partials/CommentAvatar';
import FormTitle from '../typography/FormTitle';
import FormControl from './FormControl';
import IdentifyForm from './IdentifyForm';

import styles from '../../styles/forms/comment-form.module.css';

interface CommentFormProps {
    commentableKey: string | null;
    id?: string | null;
    body?: string | null;
    parent?: string | null;
    author?: CommentAuthor | null;
    maxLength?: number | null;
    className?: string;
    focused?: boolean;
    onComplete?: (newComment: Comment, parentComment?: Comment | null) => void | null;
    onCancel?: () => void | null;
}

function CommentForm({
    commentableKey,
    id = null,
    body: initialBody = null,
    parent = null,
    author = null,
    maxLength: providedMaxLength = null,
    className,
    focused = false,
    onComplete = null,
    onCancel = null,
}: CommentFormProps) {
    const commentsMaxLength = useSetting('commentsMaxLength', 1500);
    const maxLength = providedMaxLength || commentsMaxLength || 1500;
    const { createCommentAsync } = useCreateComment(commentableKey);
    const { updateCommentAsync } = useUpdateComment(id);
    const user = useUser();
    const { name = null, hasCommented = false, confirmed = false } = user || {};
    const { name: authorName = null } = author || {};
    const confirmedUser = user !== null && confirmed;
    const isEdit = id !== null;
    const [nameEditable, setNameEditable] = useState(false);
    const [nameClicked, setNameClicked] = useState(false);
    const [formValue, setFormValue] = useState<{
        name?: string | null;
        body?: string | null;
    }>(isEdit ? { name: authorName } : {});
    const hasName = !isEmpty(name);
    const postForm = (action, data) =>
        isEdit
            ? updateCommentAsync({
                  ...(data as CreateCommentRequest),
              })
            : createCommentAsync({
                  ...(parent !== null ? { parent } : {}),
                  ...(data as CreateCommentRequest),
              });
    const onFormComplete = ({ comment: newComment, parent: parentComment = null }) => {
        setFormValue(null);
        setNameEditable(false);
        setNameClicked(false);
        if (onComplete !== null) {
            onComplete(newComment, parentComment);
        }
    };
    const { fields, onSubmit, setErrors } = useForm({
        fields: ['body', 'parent', 'name'],
        postForm,
        value: formValue,
        setValue: setFormValue,
        onComplete: onFormComplete,
    });

    // useEffect(() => {
    //     const { name: formName = null } = formValue || {};
    //     if (hasName && formName === null && !hasCommented) {
    //         setFormValue({
    //             ...formValue,
    //             name,
    //         });
    //     }
    // }, [hasName, name, formValue]);

    const nameFieldRef = useRef<HTMLInputElement>(null);
    const bodyFieldRef = useRef<HTMLTextareaElement>(null);
    const onClickEditName = () => {
        const newNameEditable = !nameEditable;
        setNameEditable(newNameEditable);
        setNameClicked(true);
        const { name: formName, ...formWithoutName } = formValue || {};
        setFormValue(
            newNameEditable
                ? {
                      ...formWithoutName,
                      name,
                  }
                : formWithoutName,
        );
    };
    const canEditName = confirmedUser && (!hasCommented || nameEditable || !hasName);

    const { logout } = useAuthLogout();

    const onClickLogout = () => {
        logout();
        setNameEditable(false);
        setFormValue(null);
    };

    useEffect(() => {
        if (confirmedUser && (!hasName || !hasCommented) && !nameEditable && !isEdit) {
            setNameEditable(true);
            setFormValue({
                ...formValue,
                name,
            });
        }
    }, [user]);

    useEffect(() => {
        if (!isEdit) {
            return;
        }
        import('turndown').then(({ default: TurndownService }) => {
            const turndownService = new TurndownService();
            const markdown = turndownService.turndown(initialBody);
            setFormValue((prev) => ({
                ...prev,
                body: markdown,
            }));
        });
    }, [id, initialBody]);

    useEffect(() => {
        if (nameEditable && nameClicked) {
            nameFieldRef.current?.focus();
        }
    }, [nameEditable, nameClicked]);

    const { onChange: bodyOnChange } = fields.body;
    const onBodyChange = (value: string | null, e) => {
        e.currentTarget.scrollTop = e.currentTarget.scrollHeight;
        bodyOnChange(value);
    };

    const [bodyFocused, setBodyFocused] = useState(false);
    const onBodyFocus = () => {
        setBodyFocused(true);
    };
    const onBodyBlur = () => {
        setBodyFocused(false);
    };

    useEffect(() => {
        if (focused && confirmedUser) {
            bodyFieldRef.current?.focus();
        }
    }, [focused, user]);

    let buttonLabel = 'Envoyer';
    if (id !== null) {
        buttonLabel = 'Enregistrer';
    } else if (parent !== null) {
        buttonLabel = 'Répondre';
    }

    return (
        <div className={classNames([styles.container, className])}>
            {!confirmedUser ? (
                <div className={styles.login}>
                    <FormTitle className={styles.title}>
                        Vous voulez commenter?
                        <br />
                        Identifiez-vous! <small>(c’est gratuit)</small>
                    </FormTitle>
                    <IdentifyForm
                        source={`comments:${commentableKey}`}
                        className={styles.loginForm}
                        withVerification
                    />
                </div>
            ) : (
                <form className={styles.form} onSubmit={onSubmit}>
                    {isEdit ? (
                        <CommentAvatar className={styles.avatar} author={author} />
                    ) : (
                        <AvatarButton className={styles.avatar} />
                    )}

                    <FormControl
                        {...fields.name}
                        disabled={user === null}
                        className={classNames([
                            styles.nameFormControl,
                            {
                                [styles.editable]: nameEditable,
                            },
                        ])}
                    >
                        <div className={styles.nameFieldInner}>
                            <AccountIcon circleColor="transparent" className={styles.icon} />
                            {nameEditable ? (
                                <TextField
                                    {...(canEditName
                                        ? fields.name
                                        : {
                                              name: 'name',
                                              value: name,
                                          })}
                                    placeholder="Votre nom"
                                    ref={nameFieldRef}
                                    className={styles.nameField}
                                    disabled={!canEditName}
                                />
                            ) : (
                                <div className={styles.nameField}>{isEdit ? authorName : name}</div>
                            )}
                            {hasCommented && hasName && !isEdit ? (
                                <RoundedButton
                                    onClick={onClickEditName}
                                    className={classNames([styles.smallButton, styles.editButton])}
                                >
                                    {nameEditable ? 'Annuler' : 'Changer'}
                                </RoundedButton>
                            ) : null}
                            {!isEdit ? (
                                <RoundedButton
                                    onClick={onClickLogout}
                                    className={classNames([
                                        styles.smallButton,
                                        styles.logoutButton,
                                    ])}
                                >
                                    Se déconnecter
                                </RoundedButton>
                            ) : null}
                        </div>
                    </FormControl>
                    <FormControl
                        {...fields.body}
                        disabled={user === null}
                        className={classNames([
                            styles.bodyFormControl,
                            {
                                [styles.focus]: bodyFocused,
                            },
                        ])}
                    >
                        <TextareaField
                            {...fields.body}
                            ref={bodyFieldRef}
                            onChange={onBodyChange}
                            onFocus={onBodyFocus}
                            onBlur={onBodyBlur}
                            maxLength={maxLength}
                            disabled={user === null}
                            placeholder="Votre commentaire..."
                            className={styles.bodyField}
                        />
                        <div className={styles.toolbar}>
                            <div className={styles.characterCount}>
                                {fields.body.value?.length || 0} / {maxLength}
                            </div>
                            {isEdit ? (
                                <FormButton
                                    type="button"
                                    onClick={onCancel}
                                    className={classNames([
                                        styles.smallButton,
                                        styles.cancelButton,
                                    ])}
                                >
                                    Annuler
                                </FormButton>
                            ) : null}
                            <FormButton
                                type="submit"
                                disabled={user === null}
                                className={classNames([styles.smallButton, styles.submitButton])}
                            >
                                {buttonLabel}
                            </FormButton>
                        </div>
                    </FormControl>
                </form>
            )}
        </div>
    );
}

export default CommentForm;
